/*----------------------------------
- WATCHLISTS FEATURE PAGE
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import React from 'react';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { PRODUCT_WATCHLISTS_PAGE_CONTENT } from '@/client/catalogs/landing/product/watchlists/pageContent';
import ProductFeaturePage from '@/client/pages/landing/product/components/ProductFeaturePage';
import { buildProductWatchlistsGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';
import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';

/*----------------------------------
- TYPES
----------------------------------*/

type ProductWatchlistsPageProps = {
    navigation?: LandingHeaderDomainsNavigation;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'product_watchlists';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * Watchlists is one of the five features the shared feature template renders. The page
 * owns nothing but its content, its conversion identity and its GEO context: the
 * layout, the tab switcher and the sections live in `ProductFeaturePage`.
 */
const ProductWatchlistsPage = ({ navigation }: ProductWatchlistsPageProps) => {
    const geoContext = React.useMemo(() => buildProductWatchlistsGeoContext(), []);
    const askAiContext = React.useMemo<AskYourAIProps>(
        () => ({
            pageType: 'guide',
            intent: 'evaluate_product',
            pageUrl: LANDING_WEBSITE_PAGE_ROUTES.productWatchlists,
            pageTitle: 'Unique Domains Watchlists feature',
            geoContext,
        }),
        [geoContext],
    );

    return (
        <ProductFeaturePage
            feature="watchlists"
            content={PRODUCT_WATCHLISTS_PAGE_CONTENT}
            navigation={navigation}
            trackingPage={TRACKING_PAGE}
            surface="product_watchlists_page"
            currentPath={LANDING_WEBSITE_PAGE_ROUTES.productWatchlists}
            attribution="website_product_watchlists_hero"
            investorTriggerId="website_product_watchlists_hero_investor"
            founderTriggerId="website_product_watchlists_hero_founder"
            planTriggerId="website_product_watchlists_plan_pro"
            finalTriggerId="website_product_watchlists_final_get_started"
            askAiContext={askAiContext}
            geoContext={geoContext}
            geoSurface="product_watchlists"
        />
    );
};

export default ProductWatchlistsPage;
